import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, qs } from '../lib/api'
import { useAuth } from '../lib/auth'
import { ago, km, sentence } from '../lib/format'
import type { Device, DeviceModel, ListResponse, Tenant } from '../lib/types'
import { Page } from '../components/Shell'
import { Icon } from '../components/Icon'
import { DataTable, Toolbar, useSearch, type Column } from '../components/DataTable'
import { Button, Chip, Field, Modal, Panel, toast } from '../components/ui'
import { DeviceEditor } from '../components/DeviceEditor'

const STATUS_TONE: Record<string, 'ok' | 'accent' | 'warn' | 'critical' | 'muted'> = {
  active: 'ok',
  assigned: 'accent',
  stock: 'muted',
  suspended: 'warn',
  retired: 'critical',
}

/**
 * Device inventory and the stock pool (FSD 3.1 / 3.2).
 *
 * The important action here is the transfer: hardware moves from the
 * platform's global pool to a reseller, and from a reseller to one of its
 * clients. That movement is what makes a unit billable, so every move is
 * logged.
 */
export function Devices() {
  const { can, user } = useAuth()
  const qc = useQueryClient()

  const search = useSearch()
  const [status, setStatus] = useState('')
  const [source, setSource] = useState('')
  const [offset, setOffset] = useState(0)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [transferring, setTransferring] = useState(false)
  const [adding, setAdding] = useState(false)
  const [editingDevice, setEditingDevice] = useState<Device | null>(null)
  const [deletingDevice, setDeletingDevice] = useState<Device | null>(null)
  const limit = 50

  const list = useQuery({
    queryKey: ['devices', search.debounced, status, source, offset],
    queryFn: () =>
      api<ListResponse<Device>>(`/devices${qs({ q: search.debounced, status, source, limit, offset })}`),
  })

  const setStatusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api(`/devices/${id}/status`, { body: { status } }),
    onSuccess: () => {
      toast('Device status updated', 'ok')
      void qc.invalidateQueries({ queryKey: ['devices'] })
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const rows = list.data?.items ?? []
  const canTransfer = can('device.assign')
  const canDelete = can('device.delete')

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const columns: Column<Device>[] = [
    ...(canTransfer
      ? [
          {
            key: 'select',
            width: '34px',
            header: (
              <input
                type="checkbox"
                checked={rows.length > 0 && rows.every((d) => selected.has(d.id))}
                onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((d) => d.id)) : new Set())}
                aria-label="Select all"
              />
            ),
            render: (d: Device) => (
              <input
                type="checkbox"
                checked={selected.has(d.id)}
                onChange={() => toggle(d.id)}
                aria-label={`Select ${d.imei}`}
              />
            ),
          },
        ]
      : []),
    {
      key: 'imei',
      header: 'IMEI',
      sort: 'imei',
      render: (d) => (
        <div>
          <div className="tnum text-[var(--color-ink)]">{d.imei}</div>
          {d.iccid && <div className="tnum text-[10px] text-[var(--color-ink-4)]">SIM {d.iccid}</div>}
        </div>
      ),
    },
    {
      key: 'model',
      header: 'Hardware',
      sort: 'model',
      render: (d) => (
        <div>
          <div>{d.modelName ? `${d.vendorName} ${d.modelName}` : '—'}</div>
          <div className="text-[11px] text-[var(--color-ink-4)]">
            {[
              d.source ? `via ${d.source} gateway` : d.protocol,
              d.capabilities?.includes('video') ? `${d.videoChannels} cam` : undefined,
              d.capabilities?.includes('recovery') ? 'recovery' : undefined,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
      ),
    },
    {
      key: 'holder',
      header: 'Held by',
      render: (d) => d.tenantName ?? '—',
    },
    {
      key: 'vehicle',
      header: 'Fitted to',
      render: (d) =>
        d.vehicleName ? (
          <Chip tone={d.vehicleRole === 'recovery' ? 'warn' : 'accent'}>
            {d.vehicleRole && d.vehicleRole !== 'primary'
              ? `${d.vehicleRole === 'camera' ? 'Camera' : 'Recovery'} · ${d.vehicleName}`
              : d.vehicleName}
          </Chip>
        ) : (
          <span className="text-[var(--color-ink-4)]">In stock</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sort: 'status',
      width: '100px',
      render: (d) => <Chip tone={STATUS_TONE[d.status] ?? 'muted'}>{d.status}</Chip>,
    },
    {
      key: 'odo',
      header: 'Odometer',
      numeric: true,
      render: (d) => km(d.totalOdometerM, 0),
    },
    {
      key: 'seen',
      header: 'Last report',
      numeric: true,
      sort: 'seen',
      width: '120px',
      render: (d) => (
        <span
          style={{
            color:
              d.lastSeenAt && Date.now() - new Date(d.lastSeenAt).getTime() > 3_600_000
                ? 'var(--text-warn)'
                : undefined,
          }}
        >
          {ago(d.lastSeenAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: canDelete ? '140px' : '110px',
      render: (d) =>
        can('device.stock.manage') && (
          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              className="btn btn-ghost btn-sm px-1.5"
              title="Edit details: model, serials, SIM, capabilities, cameras"
              onClick={() => setEditingDevice(d)}
            >
              <Icon name="edit" size={13} />
            </button>
            <select
              className="field h-6 text-[11px] py-0"
              value={d.status}
              onChange={(e) => setStatusMut.mutate({ id: d.id, status: e.target.value })}
              aria-label="Change status"
            >
              {['stock', 'assigned', 'active', 'suspended', 'retired'].map((s) => (
                <option key={s} value={s}>
                  {sentence(s)}
                </option>
              ))}
            </select>
            {canDelete && (
              <button
                className="btn btn-ghost btn-sm px-1.5 text-[var(--text-critical)]"
                title="Remove this unit from the server, with all its history"
                aria-label={`Remove ${d.imei}`}
                onClick={() => setDeletingDevice(d)}
              >
                <Icon name="trash" size={13} />
              </button>
            )}
          </div>
        ),
    },
  ]

  return (
    <Page
      title="Devices"
      subtitle={
        list.data
          ? `${list.data.total} unit${list.data.total === 1 ? '' : 's'} in your pool and below`
          : undefined
      }
      actions={
        <>
          {selected.size > 0 && canTransfer && (
            <Button variant="primary" onClick={() => setTransferring(true)}>
              <Icon name="external" size={13} />
              Transfer {selected.size}
            </Button>
          )}
          {can('device.stock.manage') && (
            <Button variant={selected.size ? 'default' : 'primary'} onClick={() => setAdding(true)}>
              <Icon name="plus" size={13} />
              Register device
            </Button>
          )}
        </>
      }
    >
      <Toolbar search={search.value} onSearch={search.setValue} placeholder="IMEI, serial, vehicle…">
        <select className="field w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {['stock', 'assigned', 'active', 'suspended', 'retired'].map((s) => (
            <option key={s} value={s}>
              {sentence(s)}
            </option>
          ))}
        </select>
        <select
          className="field w-auto"
          value={source}
          onChange={(e) => {
            setSource(e.target.value)
            setOffset(0)
          }}
          aria-label="Connection"
        >
          <option value="">All connections</option>
          <option value="direct">Direct</option>
          <option value="mdvr">MDVR gateway</option>
          <option value="nlink">nlink gateway</option>
          <option value="svtag">svtag+ gateway</option>
        </select>
      </Toolbar>

      <Panel padded={false}>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(d) => d.id}
          loading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          total={list.data?.total}
          limit={limit}
          offset={offset}
          onPage={setOffset}
          empty={{
            title: 'No devices',
            body:
              user?.tier === 1
                ? 'Register hardware here, then transfer it to a reseller.'
                : 'Ask your provider to transfer hardware into your stock pool.',
          }}
        />
      </Panel>

      {deletingDevice && (
        <DeleteDeviceModal
          device={deletingDevice}
          onClose={() => setDeletingDevice(null)}
          onDone={() => {
            setDeletingDevice(null)
            setSelected((prev) => {
              const next = new Set(prev)
              next.delete(deletingDevice.id)
              return next
            })
            void qc.invalidateQueries({ queryKey: ['devices'] })
            void qc.invalidateQueries({ queryKey: ['vehicles'] })
          }}
        />
      )}

      {transferring && (
        <TransferModal
          deviceIds={[...selected]}
          onClose={() => setTransferring(false)}
          onDone={() => {
            setTransferring(false)
            setSelected(new Set())
            void qc.invalidateQueries({ queryKey: ['devices'] })
          }}
        />
      )}

      {editingDevice && <DeviceEditor device={editingDevice} onClose={() => setEditingDevice(null)} />}

      {adding && (
        <RegisterModal
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false)
            void qc.invalidateQueries({ queryKey: ['devices'] })
          }}
        />
      )}
    </Page>
  )
}

/**
 * Removing a unit is the one irreversible action on this page, so it asks the
 * platform owner to type the identifier back. Positions, events and trips are
 * erased with it; the vehicle it was fitted to keeps its own record and
 * simply has no unit afterwards.
 */
function DeleteDeviceModal({
  device,
  onClose,
  onDone,
}: {
  device: Device
  onClose: () => void
  onDone: () => void
}) {
  const [typed, setTyped] = useState('')
  const matches = typed.trim() === device.imei

  const remove = useMutation({
    mutationFn: () => api<{ note: string }>(`/devices/${device.id}`, { method: 'DELETE' }),
    onSuccess: (r) => {
      toast(`Unit ${device.imei} removed. ${r.note}`, 'ok')
      onDone()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={`Remove unit ${device.imei}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" disabled={!matches || remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? 'Removing…' : 'Remove unit'}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        <p className="text-[13px] text-[var(--color-ink-2)] leading-relaxed">
          This erases the unit from the server together with every position, event and trip it ever
          reported. It cannot be undone.
          {device.vehicleName ? (
            <>
              {' '}
              It is currently fitted to <b>{device.vehicleName}</b>, which keeps its own record but will
              have no unit afterwards.
            </>
          ) : null}
          {device.source ? (
            <>
              {' '}
              It reports through the <b>{device.source}</b> gateway, so it will reappear as stock the next
              time that gateway restarts. To keep a unit but stop it being used, set it to Retired instead.
            </>
          ) : (
            <> To keep a unit but stop it being used, set it to Retired instead.</>
          )}
        </p>

        <Field label={`Type ${device.imei} to confirm`} required>
          <input
            className="field font-mono"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={device.imei}
            autoFocus
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
      </div>
    </Modal>
  )
}

function TransferModal({
  deviceIds,
  onClose,
  onDone,
}: {
  deviceIds: string[]
  onClose: () => void
  onDone: () => void
}) {
  const [target, setTarget] = useState('')
  const [reason, setReason] = useState('')

  const tenants = useQuery({
    queryKey: ['tenants', 'children'],
    queryFn: () => api<ListResponse<Tenant>>(`/tenants${qs({ limit: 200 })}`),
  })

  const transfer = useMutation({
    mutationFn: () =>
      api<{ transferred: number }>('/devices/transfer', {
        body: { deviceIds, toTenantId: target, reason: reason.trim() },
      }),
    onSuccess: (r) => {
      toast(`${r.transferred} device${r.transferred === 1 ? '' : 's'} transferred`, 'ok')
      onDone()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={`Transfer ${deviceIds.length} device${deviceIds.length === 1 ? '' : 's'}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!target || transfer.isPending} onClick={() => transfer.mutate()}>
            {transfer.isPending ? 'Transferring…' : 'Transfer'}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        <p className="text-[13px] text-[var(--color-ink-2)] leading-relaxed">
          Moving hardware into an account makes it assignable there, and detaches it from any vehicle
          it is currently fitted to. The move is recorded against both accounts.
        </p>

        <Field label="Move to" required>
          <select className="field" value={target} onChange={(e) => setTarget(e.target.value)} autoFocus>
            <option value="">Choose an account…</option>
            {tenants.data?.items.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.kind})
              </option>
            ))}
          </select>
        </Field>

        <Field label="Reason" hint="Recorded in the transfer log">
          <input
            className="field"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Initial allocation / RMA replacement / contract end"
          />
        </Field>
      </div>
    </Modal>
  )
}

function RegisterModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ imei: '', serialNo: '', modelId: '', iccid: '', notes: '' })

  const models = useQuery({
    queryKey: ['device-models'],
    queryFn: () => api<{ items: DeviceModel[] }>('/device-models'),
  })

  const create = useMutation({
    mutationFn: () =>
      api('/devices', {
        body: {
          imei: form.imei.trim(),
          serialNo: form.serialNo.trim(),
          modelId: form.modelId || null,
          iccid: form.iccid.trim(),
          notes: form.notes.trim(),
        },
      }),
    onSuccess: () => {
      toast('Device registered', 'ok')
      onDone()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  return (
    <Modal
      open
      onClose={onClose}
      title="Register device"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={form.imei.trim().length < 8 || !form.modelId || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Registering…' : 'Register'}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        <Field label="IMEI" required hint="The unit identifies itself with this on every connection">
          <input className="field tnum" value={form.imei} onChange={set('imei')} maxLength={20} autoFocus />
        </Field>

        <Field label="Hardware model" required hint="Determines which protocol decoder runs">
          <select className="field" value={form.modelId} onChange={set('modelId')}>
            <option value="">Choose a model…</option>
            {models.data?.items.map((m) => (
              <option key={m.id} value={m.id}>
                {m.vendor} {m.model} · {m.protocol}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid sm:grid-cols-2 gap-3.5">
          <Field label="Serial number">
            <input className="field tnum" value={form.serialNo} onChange={set('serialNo')} />
          </Field>
          <Field label="SIM ICCID">
            <input className="field tnum" value={form.iccid} onChange={set('iccid')} />
          </Field>
        </div>

        <Field label="Notes">
          <textarea className="field" rows={2} value={form.notes} onChange={set('notes')} />
        </Field>
      </div>
    </Modal>
  )
}
